import React, { useState } from 'react';
import { ChevronLeft, Search, MapPin, Bed, Bath, Maximize, TrendingUp, Calendar, DollarSign, Building2, ArrowRight, Filter } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
interface DeveloperInventoryPageProps {
  onBack: () => void;
  onProjectClick?: (projectId: number) => void;
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
  onProfileClick?: () => void;
}
interface Project {
  id: number;
  name: string;
  location: string;
  image: string;
  totalUnits: number;
  availableUnits: number;
  soldUnits: number;
  priceRange: string;
  deliveryDate: string;
  completionPercentage: number;
}
export function DeveloperInventoryPage({
  onBack,
  onProjectClick,
  onNetworkClick,
  onInventoryClick,
  onRMIClick,
  onProfileClick
}: DeveloperInventoryPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  // Featured projects slider (existing functionality)
  const featuredProjects = [{
    id: 1,
    name: 'Palm Hills October',
    location: '6th of October City',
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800',
    units: 450,
    available: 120
  }, {
    id: 2,
    name: 'Mountain View iCity',
    location: 'New Cairo',
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800',
    units: 380,
    available: 85
  }, {
    id: 3,
    name: 'Hyde Park New Cairo',
    location: 'New Cairo',
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800',
    units: 520,
    available: 200
  }];
  // Developer's own projects with simplified details
  const [projects] = useState<Project[]>([{
    id: 1,
    name: 'Palm Hills October',
    location: '6th of October City',
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800',
    totalUnits: 450,
    availableUnits: 120,
    soldUnits: 330,
    priceRange: '2.5M - 8.5M EGP',
    deliveryDate: 'Q4 2025',
    completionPercentage: 75
  }, {
    id: 2,
    name: 'Mountain View iCity',
    location: 'New Cairo',
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800',
    totalUnits: 380,
    availableUnits: 85,
    soldUnits: 295,
    priceRange: '3.2M - 12M EGP',
    deliveryDate: 'Q2 2026',
    completionPercentage: 60
  }]);
  const currentFeatured = featuredProjects[currentSlide];
  return <div className="min-h-screen bg-neutral-bg pb-20 font-sans w-full" data-id="element-1482">
      <div className="w-full mx-auto bg-neutral-bg min-h-screen relative" data-id="element-1483">
        {/* Header */}
        <div className="bg-primary px-4 py-4 safe-top flex items-center justify-between sticky top-0 z-10 shadow-sm" data-id="element-1484">
          <button onClick={onBack} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-1485">
            <ChevronLeft className="w-6 h-6" data-id="element-1486" />
          </button>
          <h1 className="font-semibold text-lg text-white" data-id="element-1487">My Inventory</h1>
          <button className="p-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-1488">
            <Filter className="w-6 h-6" data-id="element-1489" />
          </button>
        </div>

        {/* Featured Projects Slider */}
        <div className="px-4 pt-6 pb-4" data-id="element-1490">
          <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-1491">
            Featured Projects
          </h2>
          <div className="relative" data-id="element-1492">
            <div className="relative h-48 rounded-2xl overflow-hidden shadow-lg cursor-pointer" onClick={() => onProjectClick && onProjectClick(currentFeatured.id)} data-id="element-1493">
              <img src={currentFeatured.image} alt={currentFeatured.name} className="w-full h-full object-cover" data-id="element-1494" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" data-id="element-1495"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4" data-id="element-1496">
                <h3 className="text-xl font-bold text-white mb-1" data-id="element-1497">
                  {currentFeatured.name}
                </h3>
                <div className="flex items-center gap-2 text-white/90 text-sm mb-2" data-id="element-1498">
                  <MapPin className="w-4 h-4" data-id="element-1499" />
                  <span data-id="element-1500">{currentFeatured.location}</span>
                </div>
                <div className="flex items-center gap-4 text-white/80 text-xs" data-id="element-1501">
                  <span data-id="element-1502">{currentFeatured.units} Total Units</span>
                  <span className="text-accent font-semibold" data-id="element-1503">
                    {currentFeatured.available} Available
                  </span>
                </div>
              </div>
            </div>

            {/* Slider dots */}
            <div className="flex justify-center gap-2 mt-4" data-id="element-1504">
              {featuredProjects.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-2 h-2 rounded-full transition-colors ${index === currentSlide ? 'bg-primary' : 'bg-gray-300'}`} data-id="element-1505" />)}
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-4" data-id="element-1506">
          <div className="relative" data-id="element-1507">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1508" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search projects..." className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-1509" />
          </div>
        </div>

        {/* Projects List - Simplified */}
        <div className="px-4 space-y-4 pb-6" data-id="element-1510">
          <h2 className="text-base font-bold text-neutral-text" data-id="element-1511">My Projects</h2>

          {projects.map(project => <div key={project.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100" data-id="element-1512">
              {/* Project Header Image */}
              <div className="relative h-48" data-id="element-1513">
                <img src={project.image} alt={project.name} className="w-full h-full object-cover" data-id="element-1514" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" data-id="element-1515"></div>

                {/* Completion Badge */}
                <div className="absolute top-4 right-4" data-id="element-1516">
                  <div className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full" data-id="element-1517">
                    <span className="text-xs font-bold text-primary" data-id="element-1518">
                      {project.completionPercentage}% Complete
                    </span>
                  </div>
                </div>

                <div className="absolute bottom-4 left-4 right-4" data-id="element-1519">
                  <h3 className="text-xl font-bold text-white mb-1" data-id="element-1520">
                    {project.name}
                  </h3>
                  <div className="flex items-center gap-2 text-white/90 text-sm" data-id="element-1521">
                    <MapPin className="w-4 h-4" data-id="element-1522" />
                    <span data-id="element-1523">{project.location}</span>
                  </div>
                </div>
              </div>

              {/* Project Content - Simplified */}
              <div className="p-5 space-y-4" data-id="element-1524">
                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-3" data-id="element-1525">
                  <div className="text-center" data-id="element-1526">
                    <Building2 className="w-5 h-5 text-gray-400 mx-auto mb-1" data-id="element-1527" />
                    <p className="text-lg font-bold text-neutral-text" data-id="element-1528">
                      {project.totalUnits}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1529">Total</p>
                  </div>
                  <div className="text-center" data-id="element-1530">
                    <TrendingUp className="w-5 h-5 text-green-500 mx-auto mb-1" data-id="element-1531" />
                    <p className="text-lg font-bold text-green-600" data-id="element-1532">
                      {project.availableUnits}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1533">Available</p>
                  </div>
                  <div className="text-center" data-id="element-1534">
                    <Building2 className="w-5 h-5 text-purple-500 mx-auto mb-1" data-id="element-1535" />
                    <p className="text-lg font-bold text-purple-600" data-id="element-1536">
                      {project.soldUnits}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1537">Sold</p>
                  </div>
                </div>

                {/* Key Info Row */}
                <div className="flex items-center justify-between py-3 border-t border-b border-gray-100" data-id="element-1538">
                  <div className="flex items-center gap-2" data-id="element-1539">
                    <DollarSign className="w-4 h-4 text-gray-400" data-id="element-1540" />
                    <div data-id="element-1541">
                      <p className="text-xs text-gray-500" data-id="element-1542">Price Range</p>
                      <p className="text-sm font-bold text-neutral-text" data-id="element-1543">
                        {project.priceRange}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-1544">
                    <Calendar className="w-4 h-4 text-gray-400" data-id="element-1545" />
                    <div data-id="element-1546">
                      <p className="text-xs text-gray-500" data-id="element-1547">Delivery</p>
                      <p className="text-sm font-bold text-neutral-text" data-id="element-1548">
                        {project.deliveryDate}
                      </p>
                    </div>
                  </div>
                </div>

                {/* View Details Button */}
                <button onClick={() => onProjectClick && onProjectClick(project.id)} className="w-full bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary text-white font-bold py-3 rounded-xl transition-all shadow-lg shadow-primary/30 flex items-center justify-center gap-2" data-id="element-1549">
                  View Full Details
                  <ArrowRight className="w-5 h-5" data-id="element-1550" />
                </button>
              </div>
            </div>)}
        </div>

        <BottomNav onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} onProfileClick={onProfileClick} activeNav="inventory" data-id="element-1551" />
      </div>
    </div>;
}